/**
 * Pure helpers for the multi-ASIN landing page's ASIN list.
 *
 * `parseAsins` turns the raw `asins` query-parameter string into an ordered,
 * validated, de-duplicated ASIN list; `serializeAsins` turns a list back into
 * that query-parameter form; `partitionAsins` splits a list into the batches
 * the product-widget loader issues one request per.
 *
 * All three are pure: inputs are passed in, nothing reads browser, session, or
 * module-level state, inputs are never mutated, and identical inputs always
 * produce identical outputs.
 */
/** Maximum number of ASINs the page renders. */
export const MAX_ASIN_COUNT = 40;
/** ASIN count carried by a single GET_PRODUCT_WIDGET request. */
export const MAX_ASINS_PER_REQUEST = 10;
/** An ASIN is exactly 10 uppercase alphanumeric characters. */
const ASIN_PATTERN = /^[A-Z0-9]{10}$/;
/**
 * Parses the raw `asins` query-parameter value into an ordered, validated,
 * de-duplicated ASIN list.
 *
 * - URL-decodes the value; if decoding throws (malformed escape sequence), the
 *   raw value is processed by the same rules instead.
 * - Splits on commas, then for each token trims surrounding whitespace and
 *   upper-cases it; a token is accepted only when the trimmed, upper-cased form
 *   matches `[A-Z0-9]{10}`. Every other token, including an empty one, is
 *   discarded.
 * - Orders accepted ASINs by first appearance, keeps the first occurrence of a
 *   repeat and drops later ones, and returns at most `MAX_ASIN_COUNT` entries.
 * - Returns an empty list when the value is absent, empty, or contains no
 *   accepted token, without throwing.
 *
 * @param raw - The raw `asins` query-parameter value (may be null/undefined)
 * @returns Ordered, distinct, validated ASIN list, capped at `MAX_ASIN_COUNT`
 */
export const parseAsins = (raw) => {
    if (!raw) {
        return [];
    }
    let decoded;
    try {
        decoded = decodeURIComponent(raw);
    }
    catch (_a) {
        // Malformed percent-encoding: fall back to the raw value (R3.1).
        decoded = raw;
    }
    const seen = new Set();
    const accepted = [];
    for (const token of decoded.split(',')) {
        const normalized = token.trim().toUpperCase();
        if (!ASIN_PATTERN.test(normalized) || seen.has(normalized)) {
            continue;
        }
        seen.add(normalized);
        accepted.push(normalized);
        if (accepted.length === MAX_ASIN_COUNT) {
            break;
        }
    }
    return accepted;
};
/**
 * Serializes an ASIN list into the `asins` query-parameter form: entries joined
 * by exactly one comma, with no whitespace and no leading or trailing
 * separator. An empty list serializes to the empty string.
 *
 * @param asins - The ASIN list to serialize
 * @returns The comma-joined query-parameter string
 */
export const serializeAsins = (asins) => {
    return asins.join(',');
};
/**
 * Partitions an ASIN list into consecutive batches of at most `size` entries,
 * preserving order. Every batch except the last contains exactly `size`
 * entries; the last contains 1 to `size`. An empty list yields no batches.
 *
 * @param asins - The ordered ASIN list to partition
 * @param size - Maximum entries per batch (defaults to `MAX_ASINS_PER_REQUEST`)
 * @returns The list of batches, in order
 */
export const partitionAsins = (asins, size = MAX_ASINS_PER_REQUEST) => {
    const batches = [];
    for (let i = 0; i < asins.length; i += size) {
        batches.push(asins.slice(i, i + size));
    }
    return batches;
};
